import {
  autoUpdate,
  offset,
  useFloating,
  useTransitionStyles,
} from "@floating-ui/react";
import type { CSSProperties } from "react";
import { useEffect, useMemo } from "react";

/**
 * Notion 스타일 gutter/grip 배치용 positioner (표 가장자리 기준).
 *
 * Notion 레퍼런스: 열 gutter는 표 **상단** 가장자리(focus 열 중앙), 행 gutter는 표
 * **좌측** 가장자리(focus 행 중앙), 셀 핸들은 셀 **우상단 모서리**에 놓인다.
 * 따라서 셀이 아니라 표(tbody) 가장자리를 기준으로 배치해야 하므로, floating-ui
 * **virtual element**(`{ getBoundingClientRect, contextElement }`)를 reference로 쓴다.
 *
 * - "col"  → 표 상단 + 열 중앙 (zero-height ref, placement "top")
 * - "row"  → 표 좌측 + 행 중앙 (zero-width ref, placement "left")
 * - "cell" → 셀 우측 보더 (cell rect, placement "right")
 *
 * `contextElement: cellEl`을 넣어야 `autoUpdate`가 관찰할 실제 엘리먼트를 가짐
 * (스크롤/리사이즈 시 gutter가 가장자리에 고정 유지됨).
 */
export function useFocusedCellHandlePositioning(
  cellEl: HTMLElement | null,
  tbodyEl: HTMLElement | null,
  orientation: "row" | "col" | "cell" | "corner",
  show: boolean,
): {
  isMounted: boolean;
  ref: (node: HTMLElement | null) => void;
  style: CSSProperties;
} {
  const { refs, floatingStyles, context, update } = useFloating({
    open: show,
    placement:
      orientation === "row"
        ? "left"
        : orientation === "col"
          ? "top"
          : orientation === "corner"
            ? "bottom-start"
            : "right",
    // col/row/cell: 가장자리에 14px hit-area 중앙 정렬(-7).
    // corner: 18px hit-zone이 표 우하단 모서리에 걸치도록 위/좌로 살짝 당김(모서리 hover 자연).
    middleware: [
      offset(
        orientation === "corner" ? { mainAxis: -6, crossAxis: -6 } : -7,
      ),
    ],
    whileElementsMounted: autoUpdate,
  });

  const { isMounted, styles } = useTransitionStyles(context);

  // 다른 행 높이 변경으로 표가 reflow되면(특히 focus 셀보다 아래 행이 커지면) focus 셀
  // 자체는 크기가 안 변해 autoUpdate가 발화하지 않아 gutter/grip이 어긋난다.
  // tbody 크기를 직접 관찰해 재포지셔닝한다.
  useEffect(() => {
    if (!show || !tbodyEl) {
      return;
    }
    const ro = new ResizeObserver(() => update());
    ro.observe(tbodyEl);
    return () => ro.disconnect();
  }, [show, tbodyEl, update]);

  useEffect(() => {
    if (!cellEl) {
      refs.setReference(null);
      return;
    }
    refs.setReference({
      contextElement: cellEl,
      getBoundingClientRect: () => {
        const c = cellEl.getBoundingClientRect();
        const t = tbodyEl?.getBoundingClientRect() ?? c;
        if (orientation === "col") {
          // 표 상단 가장자리 + focus 셀의 열 폭(zero-height)
          return new DOMRect(c.left, t.top, c.width, 0);
        }
        if (orientation === "row") {
          // 표 좌측 가장자리 + focus 셀의 행 높이(zero-width)
          return new DOMRect(t.left, c.top, 0, c.height);
        }
        if (orientation === "corner") {
          // 표 우하단 모서리의 영점(zero-size) → placement "bottom-start"로 핸들 좌상단을
          // 모서리에 맞닿게(핸들이 모서리에서 우·하로 뻗음).
          return new DOMRect(t.right, t.bottom, 0, 0);
        }
        // 셀 핸들: 셀 rect (placement "right" → 우측 보더)
        return c;
      },
    });
  }, [cellEl, tbodyEl, orientation, refs]);

  return useMemo(
    () => ({
      isMounted,
      ref: refs.setFloating,
      // display는 CSS 클래스에서 제어한다(absolute 자식 stacking).
      style: {
        ...styles,
        ...floatingStyles,
      },
    }),
    [floatingStyles, isMounted, refs.setFloating, styles],
  );
}

/**
 * 표 우하단 코너 스케일 hit-zone 배치(focus 독립, 코어 hover 상태 기반).
 *
 * focus가 아니라 `coreState.referencePosTable`(hover된 표의 DOMRect)을 기준으로 잡으므로
 * 셀을 클릭하지 않아도 표 hover만으로 코너 hit-zone이 노출된다(ExtendButton과 동일 패턴).
 * 코너에 살짝 안쪽으로 걸치도록 배치해, hover 시 마우스가 표 위에 남아 coreState가 유지된다.
 */
export function useTableCornerPositioning(
  referencePosTable: DOMRect | null,
  show: boolean,
): {
  isMounted: boolean;
  ref: (node: HTMLElement | null) => void;
  style: CSSProperties;
} {
  const { refs, floatingStyles, context } = useFloating({
    open: show,
    placement: "bottom-start",
    // 18px hit-zone을 모서리에서 안쪽(위/좌)으로 당겨 표 위에 걸치게 한다.
    middleware: [offset({ mainAxis: -12, crossAxis: -12 })],
    whileElementsMounted: autoUpdate,
  });
  const { isMounted, styles } = useTransitionStyles(context);

  useEffect(() => {
    if (!referencePosTable) {
      refs.setReference(null);
      return;
    }
    refs.setReference({
      getBoundingClientRect: () =>
        new DOMRect(referencePosTable.right, referencePosTable.bottom, 0, 0),
    });
  }, [referencePosTable, refs]);

  return useMemo(
    () => ({
      isMounted,
      ref: refs.setFloating,
      style: { ...styles, ...floatingStyles },
    }),
    [floatingStyles, isMounted, refs.setFloating, styles],
  );
}
